import React, { useCallback, useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Truck, ArrowRight, Plus, Minus, CloudRain } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Logo, LOGOS_HIDDEN } from './Logo';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories, TOP_SELLER_SLUGS } from '../data/products';
import { projects } from '../data/projects';
import { Star } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
import { isFallPrepSeason } from '../utils/season';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
// Shop-menu columns are the catalog categories, one per column. Best sellers
// are starred in place using the same slug set as the Materials Hub filter and
// the hero estimator, so the three surfaces never disagree.
const navGroups = categories;

// Per-category tints for the mobile accordion headers. Same hues the hero
// estimator's grouped dropdown uses, so a category keeps one identity across
// the site. Tinted blocks (rather than 11px green caps) are what make the
// seven groups scannable on a phone.
// One neutral treatment for every category header. Per-category hues had no
// counterpart anywhere else on the site, so they read as decoration rather than
// a system — a solid near-black block gives the same scannability and matches
// the squared badge language used on the catalog cards.
const CATEGORY_TAB_CLASS = 'bg-[#006239] text-white';
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // Shop reveals itself on mobile rather than waiting for a tap: materials are
  // what the menu is for. It opens on a delay so the nav's own entrance lands
  // first and the category list arrives as a deliberate second beat instead of
  // appearing under the finger mid-open.
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const SHOP_AUTO_OPEN_DELAY_MS = 500;
  const shopAutoOpenTimer = useRef<number | null>(null);
  const cancelShopAutoOpen = () => {
    if (shopAutoOpenTimer.current !== null) {
      window.clearTimeout(shopAutoOpenTimer.current);
      shopAutoOpenTimer.current = null;
    }
  };
  // Which category accordions are open inside the mobile Shop section. Empty by
  // default so the seven category names are all visible in one screen — the
  // previous flat list buried categories 3–7 below the fold.
  const [openCats, setOpenCats] = useState<string[]>([]);
  const allCatsOpen = openCats.length === navGroups.length;
  const toggleCat = (name: string) => setOpenCats(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  const toggleAllCats = () => setOpenCats(allCatsOpen ? [] : navGroups.map(g => g.name));
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  // Reset mobile dropdowns when the menu closes, so every open starts from the
  // same state and re-runs the timed Shop reveal. Tapping Shop yourself cancels
  // the pending timer, so a manual open is never overridden a moment later.
  useEffect(() => {
    if (isOpen) {
      cancelShopAutoOpen();
      shopAutoOpenTimer.current = window.setTimeout(() => {
        setMobileShopOpen(true);
        shopAutoOpenTimer.current = null;
      }, SHOP_AUTO_OPEN_DELAY_MS);
    } else {
      cancelShopAutoOpen();
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
      setOpenCats([]);
    }
    return cancelShopAutoOpen;
  }, [isOpen]);
  useEffect(() => {
    const onResize = () => setIsMobileNav(window.innerWidth < 768);
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // The bar keeps a single height at every scroll position. Mobile used
      // to retract on scroll-down, but the shifting height moved the logo and
      // the Shop panel's anchor mid-scroll, which read as the page jumping.
      setNavRetracted(false);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The navbar, its dropdowns, the mobile menu and the logo are deliberately
  // theme-INDEPENDENT: they always render their light-mode treatment so the
  // site's primary chrome stays visually consistent regardless of the theme
  // toggle. Everything below the nav still honours dark mode.
  const navLinkClass = 'text-stone-800';
  const dropdownLinkClass = 'text-stone-600';
  // Category labels in the Shop Materials menu use the brand green in
  // BOTH light and dark modes — matches the visual treatment customers
  // already see for the green "LIVE", "Pay on delivery", and pill chips
  // throughout the site, so the Shop section reads as on-brand instead
  // of a muted secondary palette. Contractor pages override to
  // brand-orange to preserve the contractor-specific visual identity.
  const categoryLabelClass = isContractorPage ? 'text-brand-orange' : 'text-[#17A347]';
  const {
    totalItems,
    totalPrice,
    totalDeliveryFee,
    deliveryZone,
    toggleCart
  } = useCart();
  // Once an address has resolved to a zone we know the haul cost, so the pill
  // quotes the DELIVERED total — the same number the product page and the
  // estimator lead with. Without a zone there is no fee to add, so it falls
  // back to the materials subtotal.
  const cartTotal = deliveryZone !== null && totalDeliveryFee > 0 ? totalPrice + totalDeliveryFee : totalPrice;
  const t = useT();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  // Navigate straight to a product's detail page from the Shop dropdown.
  // Previously this routed everything through the home-page Materials Hub
  // (/?cat=…&prod=…#materials), which meant clicking a specific product
  // from any non-home page dumped the customer at the hub instead of the
  // product they actually wanted. Now we go directly to /shop/:slug.
  const goToProduct = useCallback((slug: string) => {
    navigate(`/shop/${slug}`);
  }, [navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-[#ffffff] backdrop-blur-md shadow-lg' : 'bg-[#ffffff] backdrop-blur-md'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? isMobileNav ? '52px' : '73px' : isMobileNav ? '65px' : '92px',
    transition: 'height 0.3s ease'
  }} data-id="element-1347" data-mp="3ljzxbt3v9yyl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1348" data-mp="29cqyxcgws1pa">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? isMobileNav ? '52px' : '73px' : isMobileNav ? '65px' : '92px',
        transition: 'height 0.3s ease'
      }} data-id="element-1349" data-mp="24iwa5umg4wjc">
          <div className="flex-shrink-0 flex items-center gap-4" data-id="element-1350" data-mp="1un2y0ccn3523">
            <Link to="/" onClick={resetMaterials} className="relative" aria-label="206Gravel home" data-id="element-1351" data-mp="1qj3l03qkiodh">
              <motion.div className="origin-left" initial={{
              opacity: 0,
              scale: 1
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              opacity: {
                duration: 0.4,
                ease: 'easeOut'
              }
            }} data-id="element-1352" data-mp="1lm4sfpmjyphe">
                {!LOGOS_HIDDEN && (
              /* One constant mark — no scroll swap. Sized to the bar height
              (65px mobile / 92px desktop), which doesn't change on scroll.
              Nudged down 4px: the artwork carries more empty canvas below the
              wordmark than above it, so geometric centring reads high. */
              <Logo pinned className="translate-y-[4px]" imgClassName="h-[43px] max-sm:h-[26px] w-auto" invertOnLight data-id="element-1353" data-mp="13ki464a0m8f" />)}
              </motion.div>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8" data-id="element-1354" data-mp="34jrmyfyfgpis">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-1355" data-mp="39iuh35cvdbbz">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} style={{
              fontWeight: "500",
              fontFamily: "Poppins, sans-serif"
            }} data-id="element-1356" data-mp="3ei7egk1da0st">
                {t('nav.shop')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1357" data-mp="cx52pfe5qyxw" />
              </button>

              <AnimatePresence data-id="element-1358" data-mp="cv28sk9vwlnw">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="fixed left-0 right-0 mx-auto bg-[#fcfcfc] backdrop-blur-md border border-stone-200 rounded-xl shadow-xl p-6 z-50" style={{
                // Sized for six category columns. It was still 1180px wide from
                // when there were seven, which left an empty column and forced
                // headings like "Drainage & Clear Rock" to wrap awkwardly.
                width: 'min(1000px, calc(100vw - 3rem))',
                // Sits just under the Shop button rather than under the whole
                // bar, so it overlaps the navbar the same way the Resources
                // dropdown does.
                top: navRetracted && !isOpen ? '53px' : '62px'
              }} data-id="element-1359" data-mp="gxyn3yse5vm1">
                    {/* Single row of seven even columns. A dedicated "Most
                        Popular" column used to lead the row, but it repeated
                        six products that also appeared in their own category —
                        so the best sellers are now starred in place instead.
                        Anchored to the viewport rather than the Shop button so
                        the full-width panel can't run off the right edge. */}
                    <div className="grid grid-cols-6 gap-4" data-id="element-1360" data-mp="2b5spwz9fbjjn">
                      {navGroups.map(group => <div key={group.name} data-id="element-1361" data-mp="267racob7i2cx">
                          <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2`} data-id="element-1362" data-mp="2692bshh6iy0v">
                            {group.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-1363" data-mp="4hfsl23j47ey">
                            {group.products.map(prod => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(prod.slug);
                      }} className={`flex w-full items-center gap-1.5 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors text-left px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1364" data-mp="1yw35fskkkbbe">
                                <span className="whitespace-nowrap" data-id="element-1365" data-mp="2hmuh50s14dsy">{prod.navName ?? prod.name}</span>
                                {TOP_SELLER_SLUGS.has(prod.slug) && <Star className="h-3 w-3 shrink-0 fill-current text-[#04A05C]" aria-label="Best seller" data-id="element-1366" data-mp="2mkw2xtguar84" />}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    {/* Shop by Project, expanded — the guides are a real
                        browsing path for customers who know the job but not
                        the material, so they're listed rather than hidden
                        behind a single banner link. */}
                    <div className="mt-6 border-t border-stone-200 pt-4" data-id="element-1367" data-mp="tn8opg36o920">
                      <div className="mb-3 flex items-center justify-between" data-id="element-1368" data-mp="op74usxxo5ph">
                        <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider`} data-id="element-1369" data-mp="13nj2b1t4rihp">
                          Shop by Project
                        </p>
                        <Link to="/projects" onClick={() => {
                      setIsShopOpen(false);
                      collapseCard();
                    }} className="group inline-flex items-center gap-1 text-xs font-normal text-[#04A05C] hover:text-[#05894e] transition-colors" data-id="element-1370" data-mp="ayc2lzhxwmuv">
                          All project guides
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-1371" data-mp="fwwrj2rqrk40" />
                        </Link>
                      </div>
                      {/* Four across, two full rows for the eight guides —
                          seven across left a single orphaned card. */}
                      <div className="grid grid-cols-4 gap-2" data-id="element-1372" data-mp="2mov09m0si6uf">
                        {projects.map(proj => {
                      const Icon = proj.icon;
                      return <Link key={proj.slug} to={`/projects/${proj.slug}`} onClick={() => {
                        setIsShopOpen(false);
                        collapseCard();
                      }} className="flex flex-col items-start gap-2 rounded-lg bg-[#eef7f1] px-3 py-3 transition-colors hover:bg-[#d8ecdb]" data-id="element-1373" data-mp="2vngdd1nhstnt">
                              <Icon className="h-4 w-4 text-[#04A05C]" aria-hidden="true" data-id="element-1374" data-mp="3ab1guwoa4uw9" />
                              <span className="text-xs font-normal leading-snug text-stone-800" data-id="element-1375" data-mp="3efhmcwtze0ay">
                                {proj.name}
                              </span>
                              <span className="text-[11px] text-stone-600" data-id="element-1376" data-mp="1k0yjuu9fvwsx">
                                {proj.typicalYards}
                              </span>
                            </Link>;
                    })}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Resources Dropdown. Holds Help Me Choose, Compare
             Materials, DIY Guide, Delivery Prep Guide, and Contractors
             Zone links — most of which are reference content, not
             decision tools. Labelled "Resources" so it matches the
             actual contents (and matches the mobile section header). */}
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-1377" data-mp="1vf8lga6ybqsx">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} style={{
              fontWeight: "500",
              fontFamily: "Poppins, sans-serif"
            }} data-id="element-1378" data-mp="1qf41ga6u4dxy">
                {t('nav.resources')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1379" data-mp="azf01y6hf3q3" />
              </button>
              <AnimatePresence data-id="element-1380" data-mp="ch3no39zyca4">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-2 w-56 bg-[#fcfcfc] backdrop-blur-md rounded-xl shadow-xl border border-stone-200 overflow-hidden" data-id="element-1381" data-mp="chdchnjb2de8">
                    <div className="py-2" data-id="element-1382" data-mp="3um6y83iqfghx">
                      <Link to="/help" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors`} data-id="element-1383" data-mp="3uo04vitci0px">
                        Help Me Choose
                      </Link>
                      <Link to="/compare" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors`} data-id="element-1384" data-mp="3kuax0jlcna9h">
                        {t('nav.compareMaterials')}
                      </Link>
                      <Link to="/tons-to-yards" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors`} data-id="element-1385" data-mp="2s9dx1zuurdn6">
                        Tons to Yards
                      </Link>
                      <Link to="/projects" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors`} data-id="element-1386" data-mp="31c59a1vjydfv">
                        Project Guides
                      </Link>
                      <Link to="/delivery-prep" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors`} data-id="element-1387" data-mp="369ydipdaoxdi">
                        Delivery Prep Guide
                      </Link>
                      <Link to="/delivery-zones" onClick={collapseCard} className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] hover:text-brand-orange transition-colors`} data-id="element-1388" data-mp="3g0t4u85een5t">
                        Delivery Zone Map
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Blog — promoted out of the Resources dropdown to a top-level
                link so new posts are reachable in one click. */}
            <Link to="/blog" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} style={{
            fontSize: "16px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: "500"
          }} data-id="element-1389" data-mp="198pcdfc5yk30">
              Blog
            </Link>

            {/* About — a single link now that "Why shop local" is part of that
                same page, so a dropdown holding one destination is gone. */}
            <Link to="/about" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} style={{
            fontSize: "16px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: "500"
          }} data-id="element-1390" data-mp="qgd98qdorert">
              {t('nav.about')}
            </Link>
            <Link to="/contact" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} style={{
            fontSize: "16px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: "500"
          }} data-id="element-1391" data-mp="2qss8d13erl6">
              Contact
            </Link>

            <div className="hidden md:flex items-center space-x-6" data-id="element-1392" data-mp="3hfj3tngpkmws">
              {/* The delivery-zone map lives under Resources now: it's
                  reference content, and an unlabeled circle icon never said
                  what it linked to. */}
              <div className="flex items-center space-x-3" data-id="element-1393" data-mp="38gfjyh9pw3h8">
                <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1394" data-mp="33o4mrm6d2vsd" />
              </div>
              <div className="h-6 w-px bg-stone-200" data-id="element-1395" data-mp="2yorycwzudgno" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className={`relative flex items-center gap-2 transition-all ${totalItems > 0 ? 'pl-2.5 pr-3.5 py-1.5 rounded-full bg-stone-200/80 hover:bg-stone-300/80 text-stone-900' : 'p-2 text-stone-800 hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, ${cartTotal > totalPrice ? 'delivered total' : 'subtotal'} ${cartTotal.toLocaleString()}` : 'Open cart'} data-id="element-1396" data-mp="2tqqei9ulddb5">
                <div className="relative" data-id="element-1397" data-mp="14nilypyh6jlo">
                  <Truck className="w-5 h-5" data-id="element-1398" data-mp="10ldspsbxvozk" />
                  <AnimatePresence data-id="element-1399" data-mp="10k2qvy220rb3">
                    {totalItems > 0 && <motion.span initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1400" data-mp="vmks30hr3sgt">
                        {totalItems}
                      </motion.span>}
                  </AnimatePresence>
                </div>
                <AnimatePresence data-id="element-1401" data-mp="7wgq0xk7o0rx">
                  {totalItems > 0 && <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} className="text-sm font-bold whitespace-nowrap overflow-hidden" data-id="element-1402" data-mp="7w6u6tchizeb">
                      ${cartTotal.toLocaleString()}
                    </motion.span>}
                </AnimatePresence>
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-3" data-id="element-1403" data-mp="1vuhha9s8loc1">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1404" data-mp="1vwc27o3idgg4" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className={`relative flex items-center gap-1.5 transition-all ${totalItems > 0 ? 'pl-2 pr-2.5 py-1.5 rounded-full bg-stone-200/80 text-stone-900' : 'p-2 text-stone-800 hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, ${cartTotal > totalPrice ? 'delivered total' : 'subtotal'} ${cartTotal.toLocaleString()}` : 'Open cart'} data-id="element-1405" data-mp="20tu10lntafae">
              <div className="relative" data-id="element-1406" data-mp="1h3i9h0z873ti">
                <Truck className="w-5 h-5" data-id="element-1407" data-mp="2kfqqh5i1onj0" />
                <AnimatePresence data-id="element-1408" data-mp="2keo337jxl37u">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1409" data-mp="2fi7d07t68es4">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
              </div>
              {totalItems > 0 && <span className="text-xs font-bold whitespace-nowrap" data-id="element-1410" data-mp="2ail0k0l632kd">
                  ${cartTotal.toLocaleString()}
                </span>}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 hover:text-brand-orange focus:outline-none p-2" aria-label={isOpen ? 'Close menu' : 'Open menu'} data-id="element-1411" data-mp="26j3ryyag1paz">
              {isOpen ? <X className="h-6 w-6" data-id="element-1412" data-mp="3hxz52ftfzipf" /> : <Menu className="h-6 w-6" data-id="element-1413" data-mp="3pfozcls13cea" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-1414" data-mp="3ue8p6x9qt8bx">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-1415" data-mp="3uc5pflisg0u0" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-[#fcfcfc] backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-1416" data-mp="h058tuiqs82x">
              <div className="flex justify-end items-center p-6" data-id="element-1417" data-mp="zq3gi9imnz61">
                <button onClick={() => setIsOpen(false)} className="text-stone-800 hover:text-brand-orange p-2" aria-label="Close menu" data-id="element-1418" data-mp="zroek945i08n">
                  <X className="w-6 h-6" data-id="element-1419" data-mp="33831bojhadb3" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-1420" data-mp="33afqalnnlnxn">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200" data-id="element-1421" data-mp="2taot2c7pkutv">
                  {/* "Shop Materials" opens the category list; the +/− beside
                      it expands or collapses all seven at once for anyone who
                      wants to scan the whole catalog. */}
                  <div className="flex items-center justify-end gap-3" data-id="element-1422" data-mp="3h7cfkio4ixcf">
                    {mobileShopOpen && <button onClick={toggleAllCats} className="p-1 text-stone-500 hover:text-brand-orange transition-colors" aria-label={allCatsOpen ? 'Collapse all categories' : 'Expand all categories'} aria-expanded={allCatsOpen} data-id="element-1423" data-mp="3c914k4m3egsm">
                        {allCatsOpen ? <Minus className="h-5 w-5" data-id="element-1424" data-mp="8qhqc6091bgt" /> : <Plus className="h-5 w-5" data-id="element-1425" data-mp="8q7ysdzk2w90" />}
                      </button>}
                    <button onClick={() => {
                  cancelShopAutoOpen();
                  setMobileShopOpen(!mobileShopOpen);
                }} className="flex items-center justify-end text-stone-900 font-bold gap-2 text-lg" aria-expanded={mobileShopOpen} data-id="element-1426" data-mp="h2rqtvzz3m5q">
                      Shop
                      <motion.div animate={{
                    rotate: mobileShopOpen ? 180 : 0
                  }} transition={{
                    duration: 0.3,
                    ease: [0.23, 1, 0.32, 1]
                  }} data-id="element-1427" data-mp="uydadnnftdqr">
                        <ChevronDown className="w-5 h-5" data-id="element-1428" data-mp="18s4iqspfmrkw" />
                      </motion.div>
                    </button>
                  </div>
                  <AnimatePresence data-id="element-1429" data-mp="1dqfxnhul3s9z">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  height: {
                    duration: 0.5,
                    ease: [0.23, 1, 0.32, 1]
                  },
                  opacity: {
                    duration: 0.3,
                    ease: 'easeOut'
                  }
                }} className="overflow-hidden" data-id="element-1430" data-mp="1iqkexa3bgr19">
                        <div className="space-y-2 pt-4" data-id="element-1431" data-mp="1gqoe6ujz4y54">
                          {navGroups.map(group => {
                      const open = openCats.includes(group.name);
                      return <div key={group.name} data-id="element-1432" data-mp="1qpdljte8v074">
                                <button onClick={() => toggleCat(group.name)} className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${CATEGORY_TAB_CLASS}`} aria-expanded={open} data-id="element-1433" data-mp="1qp5c6bhbyd0o">
                                  <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1434" data-mp="3nigsk033rtdv" />
                                  <span data-id="element-1435" data-mp="23292x1hyyeln">{group.name}</span>
                                </button>
                                <AnimatePresence initial={false} data-id="element-1436" data-mp="280sqf6c7nyg4">
                                  {open && <motion.div initial={{
                            height: 0,
                            opacity: 0
                          }} animate={{
                            height: 'auto',
                            opacity: 1
                          }} exit={{
                            height: 0,
                            opacity: 0
                          }} transition={{
                            duration: 0.35,
                            ease: [0.23, 1, 0.32, 1]
                          }} className="overflow-hidden" data-id="element-1437" data-mp="1t9tizyn43fy4">
                                      <div className="space-y-1 py-2 pr-1" data-id="element-1438" data-mp="34oqn8frvabq2">
                                        {group.products.map(prod => <button key={prod.slug} onClick={() => {
                                setIsOpen(false);
                                goToProduct(prod.slug);
                              }} className="flex w-full items-center justify-end gap-1.5 py-2 text-base text-stone-600 hover:text-brand-orange transition-colors" data-id="element-1439" data-mp="39olb0agcil6w">
                                            {TOP_SELLER_SLUGS.has(prod.slug) && <Star className="h-3 w-3 shrink-0 fill-current text-[#04A05C]" aria-label="Best seller" data-id="element-1440" data-mp="1iqbtmuv0lbn7" />}
                                            <span data-id="element-1441" data-mp="1drs98yw2k7wy">{prod.navName ?? prod.name}</span>
                                          </button>)}
                                      </div>
                                    </motion.div>}
                                </AnimatePresence>
                              </div>;
                    })}
                        </div>

                        <div className="mt-4 space-y-2 border-t border-stone-200 pt-3" data-id="element-1442" data-mp="2z422eex2wo36">
                          <Link to="/projects" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between bg-[#d8ecdb] px-4 py-3 text-[#04A05C] transition-colors hover:bg-[#c8e5ce]" data-id="element-1443" data-mp="2u58y9d3om9fo">
                            <div className="flex-1 text-right" data-id="element-1444" data-mp="36li36nbevshl">
                              <span className="block text-sm font-bold text-[#04A05C]" data-id="element-1445" data-mp="3bfw1axhgkdpy">
                                Shop by Project
                              </span>
                              <span className="mt-0.5 block text-xs text-[#05894e]" data-id="element-1446" data-mp="2iqp4q41kdy7c">
                                Driveway, patio, drainage & more
                              </span>
                            </div>
                            <ArrowRight className="ml-3 h-4 w-4 text-[#04A05C]" data-id="element-1447" data-mp="2snaboux59jdt" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-1448" data-mp="7zxbom6wjrty">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1449" data-mp="bz65fr1xzppi">
                    {t('nav.resources')}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-1450" data-mp="17z0urzwh56s5" />
                  </button>
                  <AnimatePresence data-id="element-1451" data-mp="yaiurkw0r3pk">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1452" data-mp="y8y0zxhj3osr">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1453" data-mp="fcg4z3vnd2qp">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 hover:text-brand-orange transition-colors text-right" data-id="element-1454" data-mp="24q32sqo0jg29">
                            Help Me Choose
                          </Link>
                          <Link to="/compare" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 hover:text-brand-orange transition-colors text-right" data-id="element-1455" data-mp="7s5gmv307xen">
                            Compare Materials
                          </Link>
                          <Link to="/projects" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 hover:text-brand-orange transition-colors text-right" data-id="element-1456" data-mp="ctkzqhlmftue">
                            Project Guides
                          </Link>
                          <Link to="/delivery-prep" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 hover:text-brand-orange transition-colors text-right" data-id="element-1457" data-mp="2nle6ycslj2tq">
                            Delivery Prep Guide
                          </Link>
                          <Link to="/delivery-zones" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 hover:text-brand-orange transition-colors text-right" data-id="element-1458" data-mp="15h7ny08s5jh5">
                            Delivery Zone Map
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Seasonal campaign. Given a permanent home in the mobile
                    menu because the announcement bar can only show it on a
                    rotation up top — this is the reliable way to find it on a
                    phone. Disappears with the season. */}
                {isFallPrepSeason() && <Link to="/fall-prep" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="flex items-center justify-end gap-2 text-lg font-semibold text-[#00A85E] transition-colors hover:text-brand-orange" data-id="element-1459" data-mp="1yacopfkqt5wq">
                    <CloudRain className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1460" data-mp="2qy8h483a4bnp" />
                    Fix It Before the Rain
                  </Link>}

                {/* Blog — top-level link, matching desktop. */}
                <Link to="/blog" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1461" data-mp="2h74072u1mgq8">
                  Blog
                </Link>

                {/* Mobile About Menu - Collapsible */}
                {/* About — one destination, so no accordion. */}
                <Link to="/about" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1462" data-mp="2pfgjz5xy6vs3">
                  About
                </Link>

                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1463" data-mp="2kgx0rdaqny3f">
                  Contact
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}